import { ApiResult } from '../models/ApiResult';
import {
  DeviceData,
  Device,
  DeviceBackend,
  DevicePatch,
  DeviceInput,
  DeviceHeartBeat,
  DeviceType
} from '../models/Device';
import organizationStore from '../stores/OrganizationStore';
import { apiClient } from './ApiService';
import { serializeDeviceFromBackend } from './serializers/DeviceSerializer';
import { droneCommanderDevicesRead, type DeviceRetrieveApiResponse } from './types/api.generated';

const createDevice = (device: DeviceInput): Promise<ApiResult<Device>> => {
  const currentOrg = organizationStore.selectedOrg;
  const deviceData = { ...device };

  if (!deviceData.organization && currentOrg) {
    deviceData.organization = currentOrg.toString();
  }

  return apiClient.post<DeviceBackend>('/drone_commander/devices/', deviceData).then((backendDevice) => ({
    success: true,
    data: serializeDeviceFromBackend(backendDevice)
  }));
};

const getAllDevices = (): Promise<ApiResult<Device[]>> =>
  apiClient
    .get<{ status: number; message: string; data: { results: Device[] } }>('/drone_commander/devices/registered/')
    .then((response) => {
      if (response && response.data && Array.isArray(response.data.results)) {
        return { success: true, data: response.data.results };
      }
      throw new Error('No devices found');
    });

const getDeviceBySerialNumber = (deviceSerialNumber: string): Promise<ApiResult<{ device: Device }>> =>
  apiClient
    .get<{ status: number; message: string; device: Device }>(`/drone_commander/devices/${deviceSerialNumber}/`)
    .then((response) => {
      if (response && response.device) {
        return { success: true, data: response };
      }
      throw new Error('Device not found');
    });

const getDeviceById = async (deviceId: string): Promise<DeviceRetrieveApiResponse> => {
  const response = await droneCommanderDevicesRead(deviceId);
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Device not found');
};

const getDeviceHeartBeat = (deviceId: string): Promise<ApiResult<DeviceHeartBeat>> =>
  apiClient
    .get<{ status: number; message: string; data: DeviceHeartBeat }>(`/drone_commander/heartbeat/${deviceId}/`)
    .then((response) => {
      if (response && response.data) {
        return { success: true, data: response.data };
      }
      throw new Error('Device Heart Beat not found');
    });

const updateDevice = (device: DevicePatch): Promise<ApiResult<Device>> =>
  apiClient.patch<DeviceBackend>(`/drone_commander/devices/${device.id}/`, device).then((profile) => {
    if (profile) {
      return { success: true, data: serializeDeviceFromBackend(profile) };
    }
    throw new Error('Device not found');
  });

const deleteDevice = (deviceIds: string[]): Promise<ApiResult<Record<number, never>>> =>
  apiClient
    .delete<DeviceBackend[]>('/drone_commander/devices/bulk-remove-devices/', {
      ids: deviceIds.map((id) => parseInt(id, 10))
    })
    .then((response) => {
      if (response) {
        return { success: true, data: {} };
      }
      throw new Error('Device not found');
    });

const getDeviceData = async (deviceId: string): Promise<ApiResult<DeviceData>> => {
  const response = await apiClient.get<DeviceData>(`/drone_commander/devices/${deviceId}/`);
  return {
    success: true,
    data: response
  };
};

const getDeviceTypes = (): Promise<ApiResult<DeviceType[]>> =>
  apiClient
    .get<{ status: number; message: string; data: DeviceType[] }>('/drone_commander/device-types', {}, false)
    .then((response) => {
      if (response && response.data) {
        return { success: true, data: response.data };
      }
      throw new Error('No device types found');
    });

const registerDevice = async (deviceUuid: string): Promise<ApiResult<Device>> => {
  const backendDevice = await apiClient.put<DeviceBackend>('/drone_commander/devices/register/', {
    device_uuid: deviceUuid
  });
  return {
    success: true as const,
    data: serializeDeviceFromBackend(backendDevice)
  };
};

export const DeviceService = {
  createDevice,
  getAllDevices,
  deleteDevice,
  updateDevice,
  getDeviceData,
  getDeviceBySerialNumber,
  getDeviceById,
  getDeviceHeartBeat,
  getDeviceTypes,
  registerDevice
};
